<template>
  <div class="simulator">
    <div class="simulator-selection">
      <v-select class="select-school"
        :class="{error: inputError.school}"
        v-model="school"
        :options="schoolList"
        :clearable="false"
        placeholder="Choose a primary school">
      </v-select>
      <v-select class="select-phase"
        :class="{error: inputError.phase}"
        v-model="phase"
        :options="phaseList"
        :clearable="false"
        placeholder="Choose a phase">
      </v-select>
      <div class="registration-phases">
        <img
          src="~images/icon-info.svg"
          @mouseover="showTooltip = true"
          @mouseleave="showTooltip = false">
        <div class="registration-phases-tooltip" v-if="showTooltip">
          <img src="~images/icon-close.svg"
              @click="showTooltip = false">
          <div v-for="item in phaseExplanation">
            <p>Phase {{item.phase}}</p>
            <p v-for="point in item.eligible">{{point}}</p>
          </div>
        </div>
      </div>
    </div>

    <st-button @click="$emit('trigger')" class="simulator-submit">Simulate</st-button>

    <div class="simulator-text">
      <p v-if="riskOversubscribed != null">
        <template v-if="phase === '1'">
          All children registered under Phase 1 will be given places.
        </template>
        <template v-else-if="isExtremeValue < 0">
          There is slight chance it will go into balloting.
        </template>
        <template v-else-if="isExtremeValue > 0">
          There is high chance it will go into balloting.
        </template>
        <template v-else>
          There is a <span class="big">{{(riskOversubscribed * 100).toFixed()}}%</span> chance it will go into balloting.
        </template>
      </p>
      <p v-if="phase !== '1' && timesOversubscribed != null">
        It is expected to be oversubscribed by <span class="big">{{timesOversubscribed.toFixed(1)}} times</span>.
      </p>
    </div>

    <div class="simulator-chart-container" v-if="historical && historical.length > 0">
      <h6 class="simulator-chart-title">Number of applicants in the past 3 years</h6>
      <bar-chart :data="historical"></bar-chart>
      <div class="simulator-chart-legend">
        <span>Vacancies</span>
        <span>Applicants</span>
        <span>Oversubscribed</span>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from './BarChart.vue'
import {schoolList, phaseList} from './constants.js'
import vSelect from 'vue-select'
import StButton from './Button'

const serviceURL = 'https://k57kq477w1.execute-api.ap-southeast-1.amazonaws.com/' + process.env.NODE_ENV

const _schoolList = Object.keys(schoolList).map(school => ({
  label: capitalize(school),
  value: school
}))

export default {
  components: {
    BarChart,
    vSelect,
    StButton
  },
  props: ['preSelected', 'info'],
  data () {
    const school = this.preSelected && this.preSelected.school &&
                   _schoolList.filter(school => school.value === this.preSelected.school)[0]
    const phase = this.preSelected && this.preSelected.phase
    return {
      school: school,
      phase: phase,
      riskOversubscribed: null,
      timesOversubscribed: null,
      historical: null,
      isExtremeValue: 0,
      inputError: {},
      showTooltip: false
    }
  },
  computed: {
    phaseExplanation () {
      return this.info.phases
    },
    schoolList () {
      return _schoolList
    },
    phaseList () {
      return phaseList
    },
    chartRange () {
      return this.historical && [0, this.historical.reduce((max, d) => {
        const _max = Math.max(d.vacancies, d.applicants)
        return _max > max ? _max : max
      }, 0)]
    }
  },
  methods: {
    simulate () {
      if (!this.school || !this.phase) {
        // this.riskOversubscribed = null
        // this.timesOversubscribed = null
        // this.historical = null
        // this.isExtremeValue = 0
        this.inputError = {
          school: !this.school,
          phase: !this.phase
        }
        return
      }

      this.inputError = {}
      const url = `${serviceURL}?school=${this.school.value}&phase=${this.phase}&_=${Date.now()}`
      fetch(url).then(res => res.json()).then(json => {
        this.historical = null
        const overallRisk = _sumBy(json.simulated, d => d.applicants > d.vacancies) / json.simulated.length
        if (overallRisk < 0.2) this.isExtremeValue = -1
        else if (overallRisk > 0.95) this.isExtremeValue = 1
        else this.isExtremeValue = 0
        this.animateResult(json)
      })
    },
    animateResult (result, frame = 0, skip = 3) {
      if (frame < result.simulated.length) {
        this.riskOversubscribed = _sumBy(result.simulated.slice(0, frame + 1),
          d => d.applicants > d.vacancies) / result.simulated.length
        this.timesOversubscribed = result.simulated[frame].applicants / result.simulated[frame].vacancies
        window.requestAnimationFrame(() => {
          this.animateResult(result, frame + skip, skip)
        })
      } else {
        this.timesOversubscribed = _meanBy(result.simulated, d => d.applicants / d.vacancies)
        result.historical.forEach(row => {
          row.label = row.year
          row.reserved = row.places - row.filled - row.vacancies
        })
        this.historical = result.historical
        this.$emit('showing')
      }
    }
  }
}

function _sumBy (arr, f) {
  return arr.reduce((sum, v) => sum + f(v), 0)
}

function _meanBy (arr, f) {
  return _sumBy(arr, f) / arr.length
}

function capitalize (str) {
  return str
    .split(' ')
    .map(token => token[0].toUpperCase() + token.slice(1).toLowerCase())
    .map(token => token.replace(/^Chij$/, 'CHIJ'))
    .map(token => token.replace(/^Anglo-chinese$/, 'Anglo-Chinese'))
    .map(token => token.replace(/ \(([a-z])/g, (match, p1) => '(' + p1.toUpperCase()))
    .join(' ')
}
</script>

<style lang="scss">
.simulator {
  padding: 0 20px;
  box-sizing: border-box;
  position: relative;
  overflow: visible;

  @media only screen and (max-width: $d_medium - 1) {
    padding: 0;
  }

  .simulator-selection {
    margin: 0 -2% 10px;

    .error .vs__dropdown-toggle {
      border-color: $red;
    }

    &::after {
      content: "";
      display: table;
      clear: both;
    }

    input {
      font-family: $font-CuratorRegular;
    }

    .select-school,
    .select-phase {
      float: left;
      margin: 0 2% 10px;
    }

    .select-school {
      width: 47%;
    }

    .select-phase {
      width: 35%;
    }

    @media only screen and (max-width: $d_medium - 1) {
      .select-school {
        width: 96%;
      }

      .select-phase {
        width: 83%;
      }
    }

    .registration-phases {
      display: inline-block;
      font-family: $font-CuratorRegular;
      font-size: 15px;
      cursor: pointer;

      img {
        width: 30px;
        margin-top: 5px;
        vertical-align: bottom;
      }

      @media only screen and (max-width: $d_medium - 1) {
        img {
          width: 30px;
          margin-top: 0;
        }
      }

      > div {
        top: 40px;
        left: 0;
        background-color: white;
        text-align: left;
        padding: 10px;
        position: absolute;
        z-index: 99;
        margin: 5px;
        box-shadow: 1px 1px 5px #cccbcb;

        @media only screen and (max-width: $d_medium - 1) {
          top: 90px;
        }

        img {
          display: none;

          @media only screen and (max-width: $d_medium - 1) {
            display: block;
            float: right;
          }
        }

        > div {
          margin-bottom: 10px;

          p:first-of-type {
            font-family: $font-CuratorBold;
          }

          p {
            font-family: $font-CuratorRegular;
            margin-bottom: 0;
            font-size: 13px;
            line-height: 18px;
          }
        }
      }
    }
  }

  .simulator-submit {
    font-family: $font-CuratorRegular;
    background-color: rgba(86,200,206,0.5);
    color: $black;
    margin-bottom: 20px;
  }

  .simulator-text {
    p:first-of-type {
      margin-bottom: 0;
    }
  }

  .simulator-chart-container {
    font-family: $font-CuratorRegular;
    font-size: 15px;
    min-height: 232px;
    margin-bottom: 30px;
  }

  .simulator-chart-legend {
    margin-top: 20px;
    padding-right: 16px;
    font-size: 0.8em;

    span {
      display: inline-block;
      margin-right: 0.8em;

      @media only screen and (max-width: $d_medium - 1) {
        display: block;
      }

      &::before {
        content: "";
        display: inline-block;
        width: 10px;
        height: 10px;
        margin-right: 0.5em;
      }

      &:nth-child(1)::before {
        background-color: rgba($themeColor,20%);
        opacity: 1;
      }

      &:nth-child(2)::before {
        background-color: $themeColor;
        opacity: 1;
      }

      &:nth-child(3)::before {
        background-color: $red;
      }
    }
  }

  .vs__selected {
    font-family: $font-CuratorRegular;
  }

  span.big {
    //font-size: 32px;
    font-family: $font-SelaneForty;
  }
}
</style>

<style lang="scss">
  .v-select {
    position: relative;
    font-family: sans-serif;
    font-size: 0.75em;
  }

  .v-select,
  .v-select * {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  /* Open Indicator */

  .v-select .vs__actions {
    position: absolute;
    bottom: 9px;
    right: 10px;
    display: inline-block;
    cursor: pointer;
    pointer-events: all;
    transition: all 150ms cubic-bezier(1.000, -0.115, 0.975, 0.855);
    transition-timing-function: cubic-bezier(1.000, -0.115, 0.975, 0.855);
    opacity: 1;
    height: 20px;
    width: 14px;
  }

  .v-select .vs__actions svg.vs__open-indicator {
    transition: all 150ms cubic-bezier(1.000, -0.115, 0.975, 0.855);
    transition-timing-function: cubic-bezier(1.000, -0.115, 0.975, 0.855);
    fill: rgba(60, 60, 60, .5);
  }

  /* Open Indicator States */

  .v-select.vs--open svg.vs__open-indicator {
    transform: rotate(180deg);
  }

  .v-select.vs--loading svg.vs__open-indicator {
    opacity: 0;
  }

  .v-select.vs--open svg.vs__open-indicator {
    bottom: 1px;
  }

  /* Dropdown Toggle */

  .v-select .vs__dropdown-toggle {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    display: block;
    padding: 5px 20px 5px 10px;
    min-height: 38px;
    background: none;
    border: 1px solid rgba(51, 51, 51, 0.6);
    border-radius: 18px;
    white-space: normal;
  }

  .v-select .vs__dropdown-toggle:after {
    visibility: hidden;
    display: block;
    font-size: 0;
    content: " ";
    clear: both;
    height: 0;
  }

  /* Clear Button */
  .v-select .vs__dropdown-toggle .vs__clear {
    position: absolute;
    bottom: 8px;
    right: 10px;
    font-size: 23px;
    font-weight: 700;
    line-height: 1;
    color: rgba(60, 60, 60, .5);
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }

  /* Dropdown Toggle States */

  .v-select.vs--searchable .vs__dropdown-toggle {
    cursor: text;
  }

  .v-select.vs--unsearchable .vs__dropdown-toggle {
    cursor: pointer;
  }

  .v-select.vs--open .vs__dropdown-toggle {
    border-bottom-color: transparent;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Dropdown Menu */

  .v-select .vs__dropdown-menu {
    display: block;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    padding: 2px 0;
    margin: 0;
    margin-top: 1px;
    width: 100%;
    max-height: 200px;
    overflow-y: scroll;
    border: 1px solid rgba(0, 0, 0, .26);
    box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, .15);
    border-top: none;
    border-radius: 0 0 4px 4px;
    text-align: left;
    list-style: none;
    background: #fff;
    font-size: 15px
  }

  .v-select .vs__dropdown-menu li {
    font-family: $font-CuratorRegular;
  }

  .v-select .vs__no-options {
    text-align: center;
  }

  /* Selected Tags */

  .v-select .vs__selected {
    color: #333;
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 0;
    margin-left: 10px;
    float: left;
    line-height: 24px;
    text-transform: capitalize;

    /* ellipsis */
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .v-select.vs--single .vs__selected {
    background-color: transparent;
    border: none;
    pointer-events: none;
  }

  .v-select.vs--single.vs--open .vs__selected {
    opacity: .5;
  }

  .v-select.vs--single.vs--open.vs--searching .vs__selected,
  .v-select.vs--single.vs--loading .vs__selected {
    display: none;
  }

  .v-select.vs--single.vs--searching:not(.vs--open):not(.vs--loading) input[type="search"] {
    opacity: .2;
  }

  /* Search Input */

  .v-select input[type="search"]::-webkit-search-decoration,
  .v-select input[type="search"]::-webkit-search-cancel-button,
  .v-select input[type="search"]::-webkit-search-results-button,
  .v-select input[type="search"]::-webkit-search-results-decoration {
    display: none;
  }

  .v-select input[type="search"]::-ms-clear {
    display: none;
  }

  .v-select input[type="search"],
  .v-select input[type="search"]:focus {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    line-height: 24px;
    font-size: 1em;
    display: inline-block;
    border: none;
    outline: none;
    margin: 0;
    margin-left: 11px;
    padding: 0;
    width: 10em;
    max-width: calc(100% - 32px);
    background: none;
    position: relative;
    box-shadow: none;
    text-overflow: ellipsis;
  }

  .v-select.vs--single input[type="search"],
  .v-select.vs--single input[type="search"]:focus {
    position: absolute;
    left: 0;
    width: 100% !important;
  }

  .v-select.vs--unsearchable input[type="search"] {
    opacity: 0;
  }

  .v-select.vs--unsearchable input[type="search"]:hover {
    cursor: pointer;
  }

  /* List Items */

  .v-select li {
    line-height: 1.42857143;
    /* Normalize line height */
    margin-bottom: 0;
    border-bottom  : 1px dotted #a7aaaa;
    max-width: 100%;
    overflow: hidden;
    display: block;
    padding: 6px 6px 4px;
    clear: both;
    color: #333;
    /* Overrides most CSS frameworks */
    text-transform: capitalize;
    position: relative;
  }

  .v-select li:hover {
    cursor: pointer;
    background: $shadowColor;
    color: $themeColor;
  }

  .v-select .vs__dropdown-menu .vs__dropdown-option--selected > li {
    color: #333;
    background: rgba(50, 50, 50, .1);
  }

  .v-select.vs--single .vs__dropdown-menu .vs__dropdown-option--selected > li {
    background: inherit;
  }

  .v-select .vs__dropdown-menu > .vs__dropdown-option--highlight > li,
  .v-select.vs--single .vs__dropdown-menu > .vs__dropdown-option--highlight > li {
    /*
       * required to override bootstrap 3's
       * .vs__dropdown-menu > li > a:hover {} styles
       */
    background: #56C8CE;
    color: #fff;
  }

  .v-select .vs__dropdown-option--highlight:not(:last-child) {
    margin-bottom: 0;
    /* Fixes Bulma Margin */
  }

  /* Loading Spinner */

  .v-select .vs__spinner {
    opacity: 0;
    position: absolute;
    top: 5px;
    right: 10px;
    font-size: 5px;
    text-indent: -9999em;
    overflow: hidden;
    border-top: .9em solid rgba(100, 100, 100, .1);
    border-right: .9em solid rgba(100, 100, 100, .1);
    border-bottom: .9em solid rgba(100, 100, 100, .1);
    border-left: .9em solid rgba(60, 60, 60, .45);
    transform: translateZ(0);
    animation: vSelectSpinner 1.1s infinite linear;
    transition: opacity .1s;
  }

  .v-select .vs__spinner,
  .v-select .vs__spinner:after {
    border-radius: 50%;
    width: 5em;
    height: 5em;
  }

  /* Disabled state */

  .v-select.vs--disabled .vs__dropdown-toggle,
  .v-select.vs--disabled .vs__dropdown-toggle .vs__clear,
  .v-select.vs--disabled .vs__dropdown-toggle input,
  .v-select.vs--disabled svg.vs__open-indicator {
    cursor: not-allowed;
    background-color: rgb(248, 248, 248);
  }

  /* Loading Spinner States */

  .v-select.vs--loading .vs__spinner {
    opacity: 1;
  }

  /* KeyFrames */

  @-webkit-keyframes vSelectSpinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes vSelectSpinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Dropdown Default Transition */

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .15s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
</style>
